/**
 * #478 Cyberpunk Neon Effect (赛博朋克霓虹效果)
 *
 * 设计灵感：2026 赛博朋克视觉趋势
 * - 霓虹灯光效果（Neon Glow）
 * - 故障艺术（Glitch Art）
 * - 雨夜城市氛围
 * - 扫描线效果（Scanlines）
 * - 数据流（Data Rain）
 * - 赛博朋克配色（粉色/青色/紫色）
 *
 * 叙事结构（5 幕）：
 * 1. 启动序列 — 数据流 + 扫描线唤醒
 * 2. 品牌揭幕 — 主标题 Glitch 登场
 * 3. 核心理念 — 三大特性卡片依次展示
 * 4. 号召行动 — Slogan + CTA
 * 5. 尾声收束 — 全局光晕淡出
 *
 * 技术要点：
 * - 多层霓虹发光（4 层外发光 + 内发光）
 * - Glitch 故障效果（水平偏移 + 色差）
 * - 60 个数据流粒子（确定性位置 + 匀速下落）
 * - 30 个霓虹圆环（脉动动画）
 * - 扫描线叠加（60% 不透明度）
 * - Spring 物理进入动画
 * - 确定性随机算法（帧级一致性）
 *
 * 适用场景：
 * - 赛博朋克主题视频
 * - 科技品牌宣传
 * - 游戏预告片
 * - 音乐视频
 * - 未来主题内容
 *
 * @author 龙虾菌 🦞
 * @date 2026-03-09
 */

import React from 'react';
import {
	AbsoluteFill,
	useCurrentFrame,
	useVideoConfig,
	spring,
	interpolate,
} from 'remotion';

// ============================================
// 确定性随机数生成器（Seeded RNG）
// ============================================
class SeededRandom {
	private seed: number;

	constructor(seed: number) {
		this.seed = seed;
	}

	next(): number {
		this.seed = (this.seed * 9301 + 49297) % 233280;
		return this.seed / 233280;
	}
}

// ============================================
// 赛博朋克配色
// ============================================
const COLORS = {
	pink: '#ff0066',
	cyan: '#00ffff',
	purple: '#8b00ff',
	yellow: '#ffff00',
	bg1: '#0a0a1a',
	bg2: '#1a0a2e',
	bg3: '#0a1a2e',
};

// ============================================
// 数据流粒子组件（Data Rain Particle）
// 位置由 seed 确定，仅 y 轴随帧数匀速移动
// ============================================
interface DataRainParticleProps {
	x: number;
	startY: number;
	speed: number;
	length: number;
	opacity: number;
	color: string;
	frame: number;
	height: number;
}

const DataRainParticle: React.FC<DataRainParticleProps> = ({
	x,
	startY,
	speed,
	length,
	opacity,
	color,
	frame,
	height,
}) => {
	const yOffset = (startY + frame * speed) % (height + length) - length;

	return (
		<div
			style={{
				position: 'absolute',
				left: x,
				top: yOffset,
				width: 2,
				height: length,
				background: `linear-gradient(to bottom, transparent, ${color}, transparent)`,
				opacity,
				filter: `blur(1px) drop-shadow(0 0 3px ${color})`,
			}}
		/>
	);
};

// ============================================
// 霓虹圆环组件（Neon Ring）
// ============================================
interface NeonRingProps {
	x: number;
	y: number;
	radius: number;
	color: string;
	frame: number;
	delay: number;
	fps: number;
}

const NeonRing: React.FC<NeonRingProps> = ({
	x,
	y,
	radius,
	color,
	frame,
	delay,
	fps,
}) => {
	const scale = spring({
		frame: frame - delay,
		fps,
		config: { damping: 15, stiffness: 100, mass: 0.5 },
	});

	const pulse = Math.sin(frame * 0.1 + delay * 0.5) * 0.2 + 0.8;

	return (
		<div
			style={{
				position: 'absolute',
				left: x - radius,
				top: y - radius,
				width: radius * 2,
				height: radius * 2,
				borderRadius: '50%',
				border: `2px solid ${color}`,
				opacity: pulse * 0.6,
				transform: `scale(${scale})`,
				filter: `
          drop-shadow(0 0 ${radius * 0.4}px ${color})
          drop-shadow(0 0 ${radius * 0.8}px ${color})
        `,
			}}
		/>
	);
};

// ============================================
// Glitch 文字组件 — 支持居中布局
// ============================================
interface GlitchTextProps {
	text: string;
	fontSize: number;
	color: string;
	frame: number;
	fps: number;
	enterDelay: number;
	glitchIntensity?: number;
	letterSpacing?: number;
}

const GlitchText: React.FC<GlitchTextProps> = ({
	text,
	fontSize,
	color,
	frame,
	fps,
	enterDelay,
	glitchIntensity = 1,
	letterSpacing = 0,
}) => {
	const opacity = spring({
		frame: frame - enterDelay,
		fps,
		config: { damping: 200, stiffness: 100 },
	});

	const slideY = interpolate(
		spring({ frame: frame - enterDelay, fps, config: { damping: 20, stiffness: 80 } }),
		[0, 1],
		[30, 0],
	);

	const glitchCycle = Math.floor(frame / 8) % 5;
	const glitchActive = glitchCycle === 0 && frame > enterDelay;
	const glitchOffset = glitchActive ? (frame % 8 - 4) * 2 * glitchIntensity : 0;

	return (
		<div
			style={{
				position: 'relative',
				fontSize,
				fontFamily: "'Arial Black', 'Helvetica Neue', Arial, sans-serif",
				fontWeight: 900,
				color,
				opacity,
				letterSpacing,
				transform: `translateX(${glitchOffset}px) translateY(${slideY}px)`,
				textShadow: `
          0 0 10px ${color},
          0 0 20px ${color},
          0 0 40px ${color},
          0 0 80px ${color}
        `,
				textAlign: 'center',
				whiteSpace: 'nowrap',
			}}
		>
			{glitchActive && (
				<>
					<div style={{ position: 'absolute', left: -3, top: 0, color: '#ff00ff', opacity: 0.5, width: '100%' }}>
						{text}
					</div>
					<div style={{ position: 'absolute', left: 3, top: 0, color: '#00ffff', opacity: 0.5, width: '100%' }}>
						{text}
					</div>
				</>
			)}
			{text}
		</div>
	);
};

// ============================================
// 特性卡片组件（Feature Card）
// ============================================
interface FeatureCardProps {
	icon: string;
	title: string;
	description: string;
	color: string;
	frame: number;
	fps: number;
	enterDelay: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
	icon,
	title,
	description,
	color,
	frame,
	fps,
	enterDelay,
}) => {
	const progress = spring({
		frame: frame - enterDelay,
		fps,
		config: { damping: 18, stiffness: 80 },
	});

	const opacity = interpolate(progress, [0, 1], [0, 1]);
	const translateY = interpolate(progress, [0, 1], [40, 0]);
	const scale = interpolate(progress, [0, 1], [0.9, 1]);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: 10,
				padding: '24px 20px',
				width: 440,
				background: 'rgba(10, 10, 30, 0.7)',
				border: `1px solid ${color}`,
				borderRadius: 12,
				opacity,
				transform: `translateY(${translateY}px) scale(${scale})`,
				boxShadow: `
          0 0 15px rgba(${color === COLORS.pink ? '255,0,102' : color === COLORS.cyan ? '0,255,255' : '139,0,255'}, 0.3),
          inset 0 0 15px rgba(${color === COLORS.pink ? '255,0,102' : color === COLORS.cyan ? '0,255,255' : '139,0,255'}, 0.08)
        `,
			}}
		>
			<div style={{ fontSize: 40 }}>{icon}</div>
			<div
				style={{
					fontSize: 30,
					fontWeight: 700,
					color,
					fontFamily: "'Arial Black', Arial, sans-serif",
					letterSpacing: 2,
					textShadow: `0 0 8px ${color}`,
				}}
			>
				{title}
			</div>
			<div
				style={{
					fontSize: 26,
					color: 'rgba(255, 255, 255, 0.7)',
					fontFamily: 'monospace',
					textAlign: 'center',
					lineHeight: 1.5,
				}}
			>
				{description}
			</div>
		</div>
	);
};

// ============================================
// 水平扫描光线组件
// ============================================
const ScanBeam: React.FC<{ frame: number; width: number; height: number }> = ({
	frame,
	width,
	height,
}) => {
	const beamY = interpolate(frame % 120, [0, 120], [-20, height + 20]);

	return (
		<div
			style={{
				position: 'absolute',
				left: 0,
				top: beamY,
				width: '100%',
				height: 2,
				background: `linear-gradient(90deg, transparent, ${COLORS.cyan}, transparent)`,
				opacity: 0.35,
				filter: `blur(2px) drop-shadow(0 0 8px ${COLORS.cyan})`,
				pointerEvents: 'none',
			}}
		/>
	);
};

// ============================================
// 主组件：赛博朋克霓虹效果
// ============================================
export const CyberpunkNeonEffect: React.FC = () => {
	const frame = useCurrentFrame();
	const { fps, width, height } = useVideoConfig();

	// ---- 确定性粒子初始化（仅由 index 决定，不含 frame） ----
	const dataRainParticles = React.useMemo(() => {
		return Array.from({ length: 60 }, (_, i) => {
			const rng = new SeededRandom(i * 1000 + 42);
			const colorChoices = [COLORS.pink, COLORS.cyan, COLORS.purple];
			return {
				x: rng.next() * width,
				startY: rng.next() * (height + 100),
				speed: 2 + rng.next() * 4,
				length: 20 + rng.next() * 60,
				opacity: 0.3 + rng.next() * 0.5,
				color: colorChoices[Math.floor(rng.next() * 3)],
			};
		});
	}, [width, height]);

	const neonRings = React.useMemo(() => {
		return Array.from({ length: 30 }, (_, i) => {
			const rng = new SeededRandom(i * 2000 + 77);
			const colorChoices = [COLORS.pink, COLORS.cyan, COLORS.purple, COLORS.yellow];
			return {
				x: rng.next() * width,
				y: rng.next() * height,
				radius: 10 + rng.next() * 40,
				color: colorChoices[Math.floor(rng.next() * 4)],
			};
		});
	}, [width, height]);

	// ---- 时间线（帧数节点）----
	const T = {
		dataRainStart: 0,
		titleEnter: Math.round(fps * 0.8),
		subtitleEnter: Math.round(fps * 1.5),
		featuresEnter: Math.round(fps * 2.5),
		featureGap: Math.round(fps * 0.5),
		ctaEnter: Math.round(fps * 5.0),
		outroStart: Math.round(fps * 7.0),
	};

	// ---- 全局淡入 / 淡出 ----
	const globalOpacity = interpolate(
		frame,
		[0, fps * 0.3, T.outroStart, T.outroStart + fps * 1.5],
		[0, 1, 1, 0],
		{ extrapolateRight: 'clamp' },
	);

	// ---- 三大特性数据 ----
	const features = [
		{
			icon: '⚡',
			title: 'NEURAL LINK',
			description: '神经直连网络\n零延迟意念操控',
			color: COLORS.pink,
		},
		{
			icon: '🔮',
			title: 'HOLO ENGINE',
			description: '全息渲染引擎\n沉浸式空间重构',
			color: COLORS.cyan,
		},
		{
			icon: '🛡️',
			title: 'GHOST SHIELD',
			description: '幽灵级加密防护\n数据安全无死角',
			color: COLORS.purple,
		},
	];

	// ---- CTA 文字动画 ----
	const ctaProgress = spring({
		frame: frame - T.ctaEnter,
		fps,
		config: { damping: 20, stiffness: 60 },
	});
	const ctaOpacity = interpolate(ctaProgress, [0, 1], [0, 1]);
	const ctaScale = interpolate(ctaProgress, [0, 1], [0.85, 1]);

	return (
		<AbsoluteFill
			style={{
				background: `linear-gradient(to bottom, ${COLORS.bg1}, ${COLORS.bg2}, ${COLORS.bg3})`,
				overflow: 'hidden',
				opacity: globalOpacity,
			}}
		>
			{/* 背景网格 */}
			<div
				style={{
					position: 'absolute',
					width: '100%',
					height: '100%',
					backgroundImage: `
            linear-gradient(rgba(0, 255, 255, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 255, 0.08) 1px, transparent 1px)
          `,
					backgroundSize: '50px 50px',
					opacity: 0.5,
				}}
			/>

			{/* 数据流粒子 */}
			{dataRainParticles.map((p, i) => (
				<DataRainParticle key={i} {...p} frame={frame} height={height} />
			))}

			{/* 霓虹圆环 */}
			{neonRings.map((ring, i) => (
				<NeonRing key={i} {...ring} frame={frame} delay={i * 4} fps={fps} />
			))}

			{/* 水平扫描光线 */}
			<ScanBeam frame={frame} width={width} height={height} />

			{/* =================== 内容层 =================== */}
			<AbsoluteFill
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					gap: 12,
					zIndex: 10,
				}}
			>
				{/* 主标题 */}
				<GlitchText
					text="NEXUS PROTOCOL"
					fontSize={82}
					color={COLORS.pink}
					frame={frame}
					fps={fps}
					enterDelay={T.titleEnter}
					glitchIntensity={1.2}
					letterSpacing={8}
				/>

				{/* 副标题 */}
				<GlitchText
					text="下一代赛博空间操作系统  ·  2026"
					fontSize={36}
					color={COLORS.cyan}
					frame={frame}
					fps={fps}
					enterDelay={T.subtitleEnter}
					glitchIntensity={0.5}
					letterSpacing={4}
				/>

				{/* 三大特性卡片 */}
				<div
					style={{
						display: 'flex',
						gap: 28,
						marginTop: 100,
					}}
				>
					{features.map((feat, i) => (
						<FeatureCard
							key={feat.title}
							{...feat}
							frame={frame}
							fps={fps}
							enterDelay={T.featuresEnter + i * T.featureGap}
						/>
					))}
				</div>

				{/* CTA */}
				<div
					style={{
						marginTop: 48,
						opacity: ctaOpacity,
						transform: `scale(${ctaScale})`,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 12,
					}}
				>
					<div
						style={{
							fontSize: 22,
							fontWeight: 700,
							color: COLORS.yellow,
							fontFamily: "'Arial Black', Arial, sans-serif",
							letterSpacing: 6,
							textShadow: `0 0 12px ${COLORS.yellow}, 0 0 24px ${COLORS.yellow}`,
						}}
					>
						JACK IN NOW
					</div>
					<div
						style={{
							fontSize: 14,
							color: 'rgba(255, 255, 255, 0.5)',
							fontFamily: 'monospace',
							letterSpacing: 3,
						}}
					>
						nexus-protocol.cyber · LAUNCH 2026.06
					</div>
				</div>
			</AbsoluteFill>

			{/* 扫描线覆盖层 */}
			<div
				style={{
					position: 'absolute',
					width: '100%',
					height: '100%',
					background: `repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.12),
            rgba(0, 0, 0, 0.12) 1px,
            transparent 1px,
            transparent 2px
          )`,
					opacity: 0.6,
					pointerEvents: 'none',
					zIndex: 20,
				}}
			/>

			{/* 顶部和底部光晕 */}
			<div
				style={{
					position: 'absolute',
					left: 0,
					right: 0,
					top: 0,
					height: 200,
					background: `linear-gradient(to bottom, rgba(255, 0, 102, 0.25), transparent)`,
					filter: 'blur(30px)',
					zIndex: 5,
				}}
			/>
			<div
				style={{
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 0,
					height: 200,
					background: `linear-gradient(to top, rgba(0, 255, 255, 0.25), transparent)`,
					filter: 'blur(30px)',
					zIndex: 5,
				}}
			/>
		</AbsoluteFill>
	);
};
